/**
 * Chatbot Bridge — sincroniza pedidos do chatbot WhatsApp (backend MongoDB)
 * com `db.deliveryOrders` (Dexie + Firebase).
 *
 * Funcionamento:
 *  1. Publica catálogo de `neighborhoods` no backend (pra chatbot consultar taxas).
 *  2. Faz polling de `/api/webhooks/zapi/inbox/pending` a cada 12s.
 *  3. Pra cada pedido novo, insere em `db.deliveryOrders` via Dexie → hooks → Firebase.
 *  4. Marca o pedido como `consumed` no backend pra não duplicar.
 *
 * Deve ser inicializado uma única vez (em App.js ou um módulo logado).
 * Roda apenas se REACT_APP_BACKEND_URL estiver definido e Firebase config OK.
 */
import db from '@/db';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 45000;
const NEIGHBORHOODS_SYNC_INTERVAL_MS = 60000; // 1 min

let pollTimer = null;
let syncTimer = null;
let started = false;

async function publishNeighborhoods() {
  try {
    const items = await db.neighborhoods.toArray();
    if (!items.length) return;
    const payload = {
      items: items.map((n) => ({
        name: n.name,
        fee: Number(n.fee || 0),
        active: n.active !== false,
      })),
    };
    await fetch(`${API}/api/webhooks/zapi/sync/neighborhoods`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[CHATBOT BRIDGE] publishNeighborhoods falhou:', err?.message || err);
  }
}

async function fetchPendingOrders() {
  try {
    const resp = await fetch(`${API}/api/webhooks/zapi/inbox/pending?limit=20`);
    if (!resp.ok) return [];
    const data = await resp.json();
    return Array.isArray(data?.orders) ? data.orders : [];
  } catch {
    return [];
  }
}

async function consumeOrder(inboxId) {
  try {
    await fetch(`${API}/api/webhooks/zapi/inbox/${inboxId}/consume`, { method: 'POST' });
  } catch {}
}

async function injectIntoDelivery(order) {
  // Garante que não duplica (chave: customerPhone + createdAt)
  const existing = await db.deliveryOrders
    .where('customerPhone').equals(order.customerPhone || '')
    .toArray();
  const sameMinute = existing.find((o) => {
    const dt = new Date(o.createdAt || 0).getTime();
    const target = new Date(order.createdAt || 0).getTime();
    return Math.abs(dt - target) < 60000;
  });
  if (sameMinute) return null;

  const doc = {
    customerName: order.customerName || 'Cliente WhatsApp',
    customerPhone: order.customerPhone || '',
    neighborhood: order.neighborhood || '',
    address: order.address || '',
    landmark: '',
    items: Array.isArray(order.items) ? order.items.map((it) => ({
      name: it.name || '',
      quantity: Number(it.quantity || 1),
      price: 0,
      subtotal: 0,
    })) : [],
    subtotal: Number(order.subtotal || 0),
    deliveryFee: Number(order.neighborhoodFee || 0),
    total: Number(order.total || 0),
    paymentMethod: 'Pix',
    status: 'pending', // KDS/Caixa veem direto
    source: 'chatbot',
    createdAt: order.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  return db.deliveryOrders.add(doc);
}

async function pollOnce() {
  const orders = await fetchPendingOrders();
  if (!orders.length) return;
  let imported = 0;
  for (const order of orders) {
    try {
      const id = await injectIntoDelivery(order);
      if (id) imported += 1;
      await consumeOrder(order.inboxId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[CHATBOT BRIDGE] inject falhou:', err?.message || err);
    }
  }
  if (imported > 0) {
    toast.success(`📲 ${imported} novo${imported > 1 ? 's' : ''} pedido${imported > 1 ? 's' : ''} via WhatsApp`, {
      description: 'Veja em Delivery / Caixa / Cozinha',
      duration: 6000,
    });
    // Som curto pra alerta operacional
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ==');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch {}
  }
}

export function startChatbotBridge() {
  if (started || !API) return;
  started = true;
  // Primeiro ciclo após 5s (não disputa boot)
  setTimeout(() => {
    publishNeighborhoods();
    pollOnce();
  }, 5000);
  pollTimer = setInterval(pollOnce, POLL_INTERVAL_MS);
  syncTimer = setInterval(publishNeighborhoods, NEIGHBORHOODS_SYNC_INTERVAL_MS);
  // eslint-disable-next-line no-console
  console.log('[CHATBOT BRIDGE] iniciado — polling a cada', POLL_INTERVAL_MS / 1000, 's');
}

export function stopChatbotBridge() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  if (syncTimer) { clearInterval(syncTimer); syncTimer = null; }
  started = false;
}
